import { useState } from 'react';
import { PaymentMethod } from '@/lib/payment-methods/payment-method.ts';
import { Button } from '@/components/ui/button.tsx';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.tsx';
import { Trash2 } from 'lucide-react';

type PaymentMethodListProps = {
  paymentMethod: PaymentMethod | null;
  isLoading: boolean;
  onDelete: (id: string) => Promise<void>;
  isDeleting: boolean;
};

export function PaymentMethodList({
  paymentMethod,
  isLoading,
  onDelete,
  isDeleting,
}: PaymentMethodListProps) {
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleDelete = async () => {
    if (!paymentMethod) return;
    await onDelete(paymentMethod.id);
    setDeleteOpen(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-between rounded-sm border border-muted p-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-12 rounded" />
          <div className="flex flex-col gap-1">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
        <Skeleton className="h-8 w-8" />
      </div>
    );
  }

  if (!paymentMethod) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        No tienes tarjetas registradas.
      </p>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between rounded-sm border border-muted p-4">
        <div className="flex items-center gap-3">
          {paymentMethod.thumbnail && (
            <img
              src={paymentMethod.thumbnail}
              alt={paymentMethod.cardType}
              className="h-8 w-12 object-contain"
            />
          )}
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium">
              {paymentMethod.cardType} **** {paymentMethod.lastFourDigits}
            </span>
            <span className="text-xs text-muted-foreground">
              {paymentMethod.cardholderName && <>{paymentMethod.cardholderName} &middot; </>}
              Vence {String(paymentMethod.expirationMonth).padStart(2, '0')}/{paymentMethod.expirationYear}
            </span>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          title="Eliminar tarjeta"
          disabled={isDeleting}
          onClick={() => setDeleteOpen(true)}
        >
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </div>

      <Dialog open={deleteOpen} onOpenChange={(open) => !open && setDeleteOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar tarjeta</DialogTitle>
            <DialogDescription>
              Se eliminará la tarjeta terminada en {paymentMethod.lastFourDigits}. Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline-default"
              onClick={() => setDeleteOpen(false)}
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
